import React, { useEffect } from 'react';

import { Box, ButtonBase, Skeleton, Stack, styled, Typography } from '@material-ui/core';
import { SxProps } from '@material-ui/system';
import { User, UserPhoto } from '../../types';

import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { actions } from '../../services/users';
import { Link } from '@reach/router';
import timeAgo from '../../utils/timeAgo';

import adminImg from './admin.png';
import maleImg from './male.png';
import femaleImg from './female.png';


const BtnWrap = styled('div')({
  borderRadius: '50%',
  overflow: 'hidden',
});





export const getProfilePhoto = (user: User) => {
  // if (user.id === 'admin') {
  //   return adminImg;
  // }

  if (!user.photos || Object.keys(user.photos).length === 0) {
    return null;
  }

  let firstPhoto: UserPhoto | null = null;

  for (const id in user.photos) {
    if (!firstPhoto || user.photos[ id ].order < firstPhoto.order) {
      firstPhoto = user.photos[ id ];
    }
  }

  if (!firstPhoto) {
    return null;
  }

  return getPhotoFile(firstPhoto);
}

export const getPhotoFile = (photo: UserPhoto) => {
  for (const file of photo.files) {
    if (!file.original) {
      return file;
    }
  }
  return photo.files[ 0 ];
}


interface Props {
  userId: string;
  size?: number;
  showName?: boolean;
}

export const ProfileAvatar: React.FC<Props> = ({ userId, size = 50, showName }) => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.users.users[ userId ]);

  useEffect(() => {
    if (!user && userId !== 'admin') {
      dispatch(actions.loadUserDetails(userId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ userId ]);

  if (userId === 'admin') {
    return (
      <Stack direction="column" spacing={1} alignItems="center">
        <BtnWrap>
          <img src={adminImg} alt={userId} width={size} height={size} />
        </BtnWrap>
        {
          showName ? <Typography variant="body2">Admin</Typography> : null
        }
      </Stack>
    )
  }

  if (!user) {
    return <Skeleton variant="circular" width={size} height={size} />
  }

  const defaultSrc = user?.gender === 'female' ? femaleImg : maleImg;

  const photo = getProfilePhoto(user);
  const src = photo?.path ? `${process.env.REACT_APP_CDN_URL}/${photo?.path}` : defaultSrc;


  return (
    <Stack direction="column" spacing={1} alignItems="center">
      <BtnWrap>
        <ButtonBase component={Link as any} to={`/users/${userId}`}>
          <img src={src} alt={userId} width={size} height={size} />
        </ButtonBase>
      </BtnWrap>
      {
        showName ? <Typography variant="body2">{user.firstName}</Typography> : null
      }
    </Stack>
  )
}

interface PrimaryProfilePhotoProps {
  userId: string;
  width?: number;
  height?: number;
}


export const PrimaryProfilePhoto: React.FC<PrimaryProfilePhotoProps> = ({ userId, width, height }) => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.users.users[ userId ]);

  useEffect(() => {
    if (!user && userId !== 'admin') {
      dispatch(actions.loadUserDetails(userId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ userId ]);


  if (!user) {
    return <Skeleton variant="rectangular" width={width} height={height} />
  }

  const defaultSrc = user?.gender === 'female' ? femaleImg : maleImg;

  const photo = getProfilePhoto(user);
  const src = photo?.path ? `${process.env.REACT_APP_CDN_URL}/${photo?.path}` : defaultSrc;


  return (
    <Stack direction="column" spacing={1} alignItems="center">
      <ButtonBase component={Link as any} to={`/users/${userId}`}>
        <img src={src} alt={userId} width={width} height={height} />
      </ButtonBase>
    </Stack>
  )
}

interface ProfilePhotoProps {
  photo: UserPhoto;
  width?: number;
  height?: number;
  sx?: SxProps;
  showAddTime?: boolean;
}



const PhotoStatus = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: '#fff',
  borderBottomRightRadius: 5,
  padding: '3px 10px',
  lineHeight: 1,
  fontSize: 14,
});

const AddTime = styled('div')({
  position: 'absolute',
  bottom: 0,
  left: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: '#fff',
  borderTopRightRadius: 5,
  padding: '3px 10px',
  lineHeight: 1,
  fontSize: 14,
});

const Img = styled('img')({
  lineHeight: 0,
})

export const ProfilePhoto: React.FC<ProfilePhotoProps> = ({ photo, width, height, sx, showAddTime }) => {
  const file = getPhotoFile(photo);

  if (!file) {
    return null;
  }


  const ratio = file.width / file.height;

  let w = width;
  let h = height;

  if (!width && height) {
    w = height * ratio;
  }
  if (!height && width) {
    h = width / ratio;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        lineHeight: 0,
        ...sx,
      }}>
      <Img
        src={`${process.env.REACT_APP_CDN_URL}/${file.path}`}
        width={w}
        height={h}
        alt={photo.id}
      />
      <PhotoStatus>
        {photo.status}
      </PhotoStatus>

      {
        showAddTime ? <AddTime>{timeAgo(photo.createTime.toDate())}</AddTime> : null
      }
    </Box>
  )
}

interface UserNameProps {
  userId: string;
}

export const UserNameLabel: React.FC<UserNameProps> = ({ userId }) => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.users.users[ userId ]);

  useEffect(() => {
    if (!user && userId !== 'admin') {
      dispatch(actions.loadUserDetails(userId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) {
    return <Skeleton variant="text" width={100} />
  }

  return <Typography>{user.firstName} {user.lastName}</Typography>;
}